import { inTauri, registerToolSource, type Tool } from "./tools";
import { useStore } from "./store";

const DENIED_PATTERNS: RegExp[] = [
  /\brm\s+-[a-z]*r[a-z]*f[a-z]*\s+\/(?:\s|$)/i,
  /\bsudo\b/i,
  /:\(\)\s*\{\s*:\|:&\s*\}\s*;\s*:/,
  /\bmkfs(\.|\s)/i,
  /\bdd\s+if=.+of=\/dev/i,
];

function commandPrefix(cmd: string): string {
  const trimmed = cmd.trim();
  const words = trimmed.split(/\s+/).slice(0, 2);
  return words.join(" ");
}

function isDeniedHard(cmd: string): boolean {
  return DENIED_PATTERNS.some((re) => re.test(cmd));
}

async function checkApproval(command: string): Promise<"allow" | "deny"> {
  const { workspacePrefs, requestApproval, saveWorkspacePrefs } = useStore.getState();
  const cmd = command.trim();
  if (isDeniedHard(cmd)) return "deny";

  const allowed = workspacePrefs?.allowedPrefixes ?? [];
  const denied = workspacePrefs?.deniedPrefixes ?? [];
  if (denied.some((p) => cmd.startsWith(p))) return "deny";
  if (allowed.some((p) => cmd.startsWith(p))) return "allow";

  const decision = await requestApproval(cmd);
  if (decision === "deny") return "deny";
  if (decision === "always") {
    const prefix = commandPrefix(cmd);
    if (prefix && !allowed.includes(prefix)) {
      await saveWorkspacePrefs({ allowedPrefixes: [...allowed, prefix] });
    }
  }
  return "allow";
}

registerToolSource(async () => {
  if (!inTauri) return [];
  if (!useStore.getState().activeWorkspacePath) return [];
  const { invoke } = await import("@tauri-apps/api/core");

  const requireRoot = (): string => {
    const path = useStore.getState().activeWorkspacePath;
    if (!path) throw new Error("No workspace attached to this chat.");
    return path;
  };

  const resolveInRoot = (relPath: string): string => {
    const root = requireRoot();
    if (!relPath || relPath === "." || relPath === "./") return root;
    if (relPath.startsWith("/") || relPath.startsWith("~")) return relPath;
    return `${root.replace(/\/+$/, "")}/${relPath.replace(/^\/+/, "")}`;
  };

  const tools: Tool[] = [
    {
      source: "tauri",
      name: "workspace_root",
      description: "Return the absolute path of the workspace folder attached to this chat.",
      schema: { type: "object", properties: {}, additionalProperties: false },
      alwaysOn: true,
      run: async () => ({ path: requireRoot() }),
    },
    {
      source: "tauri",
      name: "workspace_list",
      description: "List entries in a workspace directory. path is relative to the workspace root (use '.' for the root).",
      requiresPermission: "fs_read",
      alwaysOn: true,
      schema: {
        type: "object",
        properties: {
          path: { type: "string", description: "Relative path under the workspace, '.' for root." },
          show_hidden: { type: "boolean" },
        },
        required: ["path"],
        additionalProperties: false,
      },
      run: async (args) => invoke("fs_list", { args: { path: resolveInRoot(String(args.path ?? ".")), show_hidden: args.show_hidden } }),
    },
    {
      source: "tauri",
      name: "workspace_read",
      description: "Read a file inside the workspace. path is relative to the workspace root.",
      requiresPermission: "fs_read",
      alwaysOn: true,
      schema: {
        type: "object",
        properties: {
          path: { type: "string" },
          max_bytes: { type: "integer" },
        },
        required: ["path"],
        additionalProperties: false,
      },
      run: async (args) => invoke("fs_read", { args: { path: resolveInRoot(String(args.path ?? "")), max_bytes: args.max_bytes } }),
    },
    {
      source: "tauri",
      name: "workspace_search",
      description: "Recursively find files in the workspace whose name matches a glob (e.g. '*.ts').",
      requiresPermission: "fs_read",
      alwaysOn: true,
      schema: {
        type: "object",
        properties: {
          pattern: { type: "string" },
          max_depth: { type: "integer" },
          limit: { type: "integer" },
          show_hidden: { type: "boolean" },
        },
        required: ["pattern"],
        additionalProperties: false,
      },
      run: async (args) => invoke("fs_search", { args: { root: requireRoot(), pattern: args.pattern, max_depth: args.max_depth, limit: args.limit, show_hidden: args.show_hidden } }),
    },
    {
      source: "tauri",
      name: "workspace_grep",
      description: "Search workspace file contents for a substring. Skips binary files.",
      requiresPermission: "fs_read",
      alwaysOn: true,
      schema: {
        type: "object",
        properties: {
          query: { type: "string" },
          file_pattern: { type: "string" },
          max_depth: { type: "integer" },
          limit: { type: "integer" },
          case_insensitive: { type: "boolean" },
          show_hidden: { type: "boolean" },
        },
        required: ["query"],
        additionalProperties: false,
      },
      run: async (args) => invoke("fs_grep", { args: { root: requireRoot(), ...args } }),
    },
    {
      source: "tauri",
      name: "workspace_write",
      description: "Write a file inside the workspace. path is relative to the workspace root. Creates parent dirs if create_parents is true.",
      requiresPermission: "fs_write",
      alwaysOn: true,
      schema: {
        type: "object",
        properties: {
          path: { type: "string", description: "Path relative to workspace root (e.g. 'src/foo.ts')." },
          content: { type: "string" },
          create_parents: { type: "boolean", description: "Create missing parent dirs (default false)." },
        },
        required: ["path", "content"],
        additionalProperties: false,
      },
      run: async (args) => invoke("workspace_write", { args: { root: requireRoot(), ...args } }),
    },
    {
      source: "tauri",
      name: "workspace_edit",
      description: "Replace exactly one occurrence of old_string with new_string in a workspace file. path is relative to the workspace root.",
      requiresPermission: "fs_write",
      alwaysOn: true,
      schema: {
        type: "object",
        properties: {
          path: { type: "string" },
          old_string: { type: "string" },
          new_string: { type: "string" },
        },
        required: ["path", "old_string", "new_string"],
        additionalProperties: false,
      },
      run: async (args) => invoke("workspace_edit", { args: { root: requireRoot(), ...args } }),
    },
    {
      source: "tauri",
      name: "shell_exec",
      description:
        "Run a shell command (sh -c) with the workspace folder as cwd. The user is prompted to approve unfamiliar commands. " +
        "Returns exit_code, stdout, stderr. Use this for build/test/git/etc. Avoid destructive commands like `rm -rf` or `sudo`.",
      requiresPermission: "shell_exec",
      alwaysOn: true,
      schema: {
        type: "object",
        properties: {
          command: { type: "string", description: "Shell command, e.g. 'bun test', 'git status'." },
          timeout_ms: { type: "integer", description: "Timeout in ms (default 30000, max 120000)." },
        },
        required: ["command"],
        additionalProperties: false,
      },
      run: async (args) => {
        const root = requireRoot();
        const command = String(args.command ?? "");
        if (!command.trim()) return { error: "empty command" };
        const decision = await checkApproval(command);
        if (decision === "deny") return { error: "Command denied by user or policy.", command };
        return invoke("shell_exec", { args: { root, command, timeout_ms: args.timeout_ms } });
      },
    },
  ];
  return tools;
});
